/**
 * Upload-status payloads.
 *
 * `GET /api/files/upload-status/{file_id}` has no response model; the dict
 * is assembled in backend/crucible-ai-service/main.py (get_upload_status),
 * and nullability comes from the `files` table columns in
 * backend/app/core/database.py. The Next.js BFF at
 * src/app/api/files/upload-status/[fileId]/route.ts passes the body through
 * verbatim and rewrites a 404 into `{error: 'Not found'}`.
 */
/**
 * Every status the backend can report. `stored` is TERMINAL, not a way-point:
 * `_process_complete_upload_background` sets it when local text extraction
 * failed but the AI Files API upload succeeded, so Claude can still read the
 * document natively. Omitting it here is what let the valet-key poll spin for
 * its full 300s timeout on a perfectly usable file and then report a false
 * `error`/`processing_timeout` — the union narrowed `stored` out of existence,
 * so the terminal check could not be written.
 */
export type UploadStatus = 'processing' | 'ready' | 'stored' | 'error'

/**
 * Statuses the backend never moves off. Anything not in here is transient and
 * must keep being polled.
 */
export const TERMINAL_UPLOAD_STATUSES = ['ready', 'stored', 'error'] as const

export function isTerminalUploadStatus(status: string | undefined): boolean {
  return (TERMINAL_UPLOAD_STATUSES as readonly string[]).includes(status ?? '')
}

export interface UploadStatusRow {
  id: string
  filename: string
  status: UploadStatus
  ai_file_id: string | null
  extracted_content: string | null
  file_size: number
  blob_path: string
  failure_reason: string | null
}

/**
 * What the valet-key poll resolves to. It re-shapes the status row into the
 * `{files, db_persisted}` envelope that `/api/files/upload` returns, so the
 * upload callers only ever handle one shape.
 *
 * `Partial<UploadStatusRow>` because the poll's own timeout branch
 * synthesises a terminal row from just `{id, filename, status,
 * failure_reason}` — it never carries the blob/extraction columns.
 */
export interface PolledUploadResult {
  files: Array<Partial<UploadStatusRow> & { filename: string }>
  db_persisted: boolean
}
